/**
 * Fans — the game page's "Fans" rail: everyone who currently likes THIS game,
 * newest first.
 *
 * The counterpart to {@link RecentPlayers}: that rail answers "does anyone
 * play this game", this one answers "does anyone love it" — and it puts faces
 * on the likes_count, which also makes it the visible proof that a like was
 * recorded (a like is one row per person; the count and this list can never
 * disagree).
 *
 * Everything that decides WHO is listed lives in the `public_game_fans` view
 * (0067): public live games only, claimed profiles only. One-shot fetch, no
 * subscription, and an empty list renders NOTHING — an unliked game should
 * read as waiting for its first fan, not as unloved.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useServices } from '../services/context.js';
import type { GameFan } from '../services/types.js';
import { UserAvatar } from './UserAvatar.js';
import { formatTimeAgo } from './relativeTime.js';

export function Fans({ gameKey }: { gameKey: string }) {
  const services = useServices();
  const [fans, setFans] = useState<GameFan[] | null>(null);

  useEffect(() => {
    let active = true;
    setFans(null); // component instances are reused across game→game navigation
    services
      .gameFans(gameKey)
      .then((rows) => {
        if (active) setFans(rows);
      })
      .catch(() => {
        // Decoration on a page that stands without it; a failed read renders
        // nothing rather than an error the visitor can't act on.
        if (active) setFans([]);
      });
    return () => {
      active = false;
    };
  }, [services, gameKey]);

  if (!fans || fans.length === 0) return null;

  return (
    <div className="panel" data-testid="game-fans">
      <h2 className="panel__title">Fans</h2>
      <ul className="recent-players">
        {fans.map((fan) => (
          <li key={fan.uid} className="recent-players__item" data-testid="game-fan">
            <UserAvatar avatarKey={fan.avatarKey} size={30} alt={`@${fan.handle}`} />
            <Link className="recent-players__handle" to={`/u/${fan.handle}`}>
              @{fan.handle}
            </Link>
            <span
              className="recent-players__when muted"
              title={new Date(fan.likedAt).toLocaleString()}
            >
              ♥ {formatTimeAgo(fan.likedAt)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
